import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { UserCheck, Play, TrendingUp, AlertTriangle, QrCode, Users, X, Download, Search, Circle, RefreshCw } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, NoEvent } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import client, { API } from "@/lib/api";
import { toast } from "sonner";

const FILL_COLORS = {
  empty: { bar: "#94a3b8", text: "text-slate-500", bg: "bg-slate-100" },
  filling: { bar: "#10b981", text: "text-emerald-600", bg: "bg-emerald-50" },
  near_full: { bar: "#f59e0b", text: "text-amber-600", bg: "bg-amber-50" },
  full: { bar: "#ef4444", text: "text-rose-600", bg: "bg-rose-50" },
  overflow: { bar: "#b91c1c", text: "text-rose-700", bg: "bg-rose-100 animate-pulse" },
  unknown: { bar: "#cbd5e1", text: "text-slate-400", bg: "bg-slate-50" },
};

const STATUS_LABEL = { upcoming: "À venir", live: "En cours", past: "Terminé", unknown: "" };
const STATUS_TINT = { upcoming: "bg-slate-100 text-slate-700", live: "bg-emerald-100 text-emerald-700 animate-pulse", past: "bg-slate-100 text-slate-500" };

function KpiCard({ icon: Icon, label, value, sub, tint, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testId}
      className={`text-left p-5 rounded-2xl border border-slate-200 bg-white transition ${onClick ? "hover:shadow-md hover:border-violet-300 cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-3xl font-bold font-display tracking-tight">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5 font-medium">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
    </button>
  );
}

function SessionCard({ session, onOpen, onQr }) {
  const fs = FILL_COLORS[session.fill_status] || FILL_COLORS.unknown;
  const pct = Math.min(100, Math.round((session.fill_rate || 0) * 100));
  const live = session.live_status === "live";
  const soon = session.live_status === "soon";
  const past = session.live_status === "past";
  const badge = live ? "En cours" : soon ? "Bientôt" : past ? "Passée" : "À venir";
  const badgeTint = live ? "bg-emerald-100 text-emerald-700 animate-pulse"
                     : soon ? "bg-amber-100 text-amber-700"
                     : past ? "bg-slate-100 text-slate-400"
                     : "bg-slate-100 text-slate-600";
  return (
    <div className={`p-4 rounded-2xl border border-slate-200 bg-white transition hover:shadow-md ${session.fill_status === "overflow" ? "border-rose-400" : ""}`} data-testid={`session-card-${session.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-display font-bold text-sm truncate">{session.title}</p>
          <p className="text-xs text-slate-500">{session.startTime} → {session.endTime} · {session.location || "—"}</p>
        </div>
        <Badge className={`shrink-0 ${badgeTint} border-0 font-medium text-[10px]`}>{badge}</Badge>
      </div>
      <div className="mb-2">
        <div className="flex items-baseline justify-between text-xs mb-1">
          <span className={`font-bold ${fs.text}`}>{session.current_checkins} / {session.capacity_max || "∞"}</span>
          {session.capacity_max ? <span className="text-slate-400">{pct}%</span> : <span className="text-slate-400">sans limite</span>}
        </div>
        {session.capacity_max ? (
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: fs.bar }} />
          </div>
        ) : <div className="h-2 rounded-full bg-slate-100" />}
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onQr(session)} data-testid={`session-qr-${session.id}`}>
          <QrCode className="h-3.5 w-3.5 mr-1" />QR
        </Button>
        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onOpen(session)} data-testid={`session-open-${session.id}`}>
          <Users className="h-3.5 w-3.5 mr-1" />Détails
        </Button>
      </div>
    </div>
  );
}

export default function CheckinsDashboard() {
  const { selectedEvent } = useData();
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [drawerSession, setDrawerSession] = useState(null);
  const [drawerAttendees, setDrawerAttendees] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState("");
  const [qrModal, setQrModal] = useState(null);
  const [alertsModal, setAlertsModal] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const loadStats = useCallback(async () => {
    if (!selectedEvent) return;
    try {
      const { data } = await client.get(`/organizer/events/${selectedEvent.id}/live-stats`);
      setStats(data);
    } catch (e) {
      // silent (dashboard poll)
    }
  }, [selectedEvent]);

  const loadTimeline = useCallback(async () => {
    if (!selectedEvent) return;
    try {
      const { data } = await client.get(`/organizer/events/${selectedEvent.id}/checkins/timeline`);
      setTimeline(data.points || []);
    } catch { /* silent */ }
  }, [selectedEvent]);

  // Initial load + polling fallback (5s)
  useEffect(() => {
    if (!selectedEvent) return;
    loadStats();
    loadTimeline();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (!wsConnected) loadStats();
      loadTimeline();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedEvent, loadStats, loadTimeline, wsConnected]);

  // WebSocket connection
  useEffect(() => {
    if (!selectedEvent) return;
    const token = localStorage.getItem("jpi_token");
    if (!token) return;
    const wsUrl = API.replace(/^http/, "ws").replace(/\/api$/, "") + `/api/organizer/ws/events/${selectedEvent.id}/checkins?token=${token}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "session_update") {
            setStats((prev) => prev ? {
              ...prev,
              sessions: prev.sessions.map((s) => s.id === msg.sessionId ? { ...s, current_checkins: msg.current_checkins, fill_rate: msg.fill_rate, fill_status: msg.fill_status } : s),
            } : prev);
          } else if (msg.type === "event_checkin") {
            setStats((prev) => prev ? {
              ...prev, kpis: { ...prev.kpis, total_event_checkins: msg.total_checkins },
            } : prev);
            loadTimeline();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => setWsConnected(false);
      ws.onerror = () => setWsConnected(false);
      return () => { try { ws.close(); } catch {} };
    } catch { setWsConnected(false); }
  }, [selectedEvent, loadTimeline]);

  const openDrawer = useCallback(async (session) => {
    setDrawerSession(session);
    setDrawerLoading(true);
    setDrawerAttendees([]);
    setDrawerSearch("");
    try {
      const { data } = await client.get(`/organizer/events/${selectedEvent.id}/sessions/${session.id}/attendees`);
      setDrawerAttendees(data.attendees || []);
    } catch { toast.error("Impossible de charger la liste"); }
    finally { setDrawerLoading(false); }
  }, [selectedEvent]);

  const openQr = useCallback(async (session) => {
    try {
      const { data } = await client.get(`/organizer/events/${selectedEvent.id}/sessions/${session.id}/qr`);
      setQrModal({ session, ...data });
    } catch { toast.error("Impossible de générer le QR"); }
  }, [selectedEvent]);

  const exportCsv = useCallback(() => {
    if (!drawerSession || !drawerAttendees.length) return;
    const rows = [["Heure de scan", "Prénom", "Nom", "Entreprise", "Fonction", "Catégorie"],
                  ...drawerAttendees.map((a) => [a.scannedAt, a.firstName, a.lastName, a.company, a.jobTitle, a.category])];
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${drawerSession.title.replace(/[^a-z0-9]/gi, "_")}_participants.csv`;
    a.click();
  }, [drawerSession, drawerAttendees]);

  const filteredAttendees = useMemo(() => {
    if (!drawerSearch) return drawerAttendees;
    const s = drawerSearch.toLowerCase();
    return drawerAttendees.filter((a) => [a.firstName, a.lastName, a.company, a.jobTitle].some((v) => (v || "").toLowerCase().includes(s)));
  }, [drawerAttendees, drawerSearch]);

  const alertSessions = useMemo(() =>
    (stats?.sessions || []).filter((s) => ["near_full", "full", "overflow"].includes(s.fill_status)),
    [stats]);

  if (!selectedEvent) return (<><PageHeader title="Suivi de présence temps réel" /><NoEvent /></>);
  if (!stats) return (<><PageHeader title="Suivi de présence temps réel" actions={<EventSelector />} /><div className="p-10 text-center text-slate-400">Chargement des statistiques…</div></>);

  const k = stats.kpis;
  const attendancePct = Math.round((k.attendance_rate || 0) * 100);

  return (
    <>
      <PageHeader
        title="Suivi de présence temps réel"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <EventSelector />
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" data-testid="ws-indicator">
              <Circle className={`h-2 w-2 ${wsConnected ? "fill-emerald-500 text-emerald-500 animate-pulse" : "fill-slate-300 text-slate-300"}`} />
              <span className="text-slate-600">{wsConnected ? "Temps réel" : "Polling"}</span>
            </div>
            <Button size="sm" variant="outline" onClick={() => { loadStats(); loadTimeline(); }} data-testid="refresh-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />Rafraîchir
            </Button>
          </div>
        }
      />

      {/* Event header */}
      <div className="rounded-xl bg-white border border-slate-200 p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="event-header">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="font-display font-bold text-lg">{stats.event.name}</p>
            <Badge className={`${STATUS_TINT[stats.status] || STATUS_TINT.upcoming} border-0`}>{STATUS_LABEL[stats.status] || "—"}</Badge>
          </div>
          <p className="text-xs text-slate-500">{stats.event.startDate} → {stats.event.endDate} · {stats.event.city}</p>
        </div>
        <p className="text-xs text-slate-400">Mise à jour : {stats.last_updated_at?.slice(11, 19)}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard
          icon={UserCheck}
          label="Participants entrés"
          value={`${k.total_event_checkins} / ${k.total_expected}`}
          sub={`Taux de présence : ${attendancePct}%`}
          tint="bg-violet-100 text-violet-700"
          testId="kpi-checkins"
        />
        <KpiCard
          icon={Play}
          label="Sessions en cours"
          value={k.sessions_in_progress}
          sub={`${k.sessions_total} sessions programmées`}
          tint="bg-blue-100 text-blue-700"
          testId="kpi-in-progress"
        />
        <KpiCard
          icon={TrendingUp}
          label="Salle la plus remplie"
          value={k.top_session ? `${Math.round((k.top_session.fill_rate || 0) * 100)}%` : "—"}
          sub={k.top_session ? k.top_session.title : "Pas de session capée"}
          tint="bg-amber-100 text-amber-700"
          onClick={k.top_session ? () => openDrawer(k.top_session) : null}
          testId="kpi-top"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Alertes de capacité"
          value={k.alerts_count}
          sub={k.alerts_count ? "Cliquez pour voir" : "Aucune alerte"}
          tint={k.alerts_count ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"}
          onClick={k.alerts_count ? () => setAlertsModal(true) : null}
          testId="kpi-alerts"
        />
      </div>

      {/* Timeline chart */}
      {timeline.length > 1 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-6" data-testid="timeline-chart">
          <p className="font-display font-bold text-sm mb-3">Arrivées cumulées</p>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={timeline}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} tickFormatter={(v) => (v || "").slice(11, 16)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} labelFormatter={(v) => (v || "").replace("T", " ").slice(0, 16)} />
              <Area type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} fill="url(#grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sessions grid */}
      <div className="mb-4 flex items-center justify-between">
        <p className="font-display font-bold text-sm">Sessions ({stats.sessions.length})</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="sessions-grid">
        {stats.sessions.map((s) => <SessionCard key={s.id} session={s} onOpen={openDrawer} onQr={openQr} />)}
        {!stats.sessions.length && <p className="text-center text-slate-400 py-10 col-span-full">Aucune session programmée. Ajoutez des activités dans <b>Programme</b>.</p>}
      </div>

      {/* Drawer session */}
      <Sheet open={!!drawerSession} onOpenChange={(v) => !v && setDrawerSession(null)}>
        <SheetContent className="sm:max-w-md w-full overflow-y-auto" data-testid="drawer-session">
          <SheetHeader>
            <SheetTitle className="pr-8">{drawerSession?.title}</SheetTitle>
            <SheetDescription>
              {drawerSession?.startTime} → {drawerSession?.endTime} · {drawerSession?.location || "—"}
            </SheetDescription>
          </SheetHeader>
          {drawerSession && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-3 rounded-xl bg-slate-50">
                  <p className="text-2xl font-bold font-display">{drawerSession.capacity_max || "∞"}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Attendus</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-violet-50">
                  <p className="text-2xl font-bold font-display text-violet-700">{drawerSession.current_checkins}</p>
                  <p className="text-[10px] text-violet-600 uppercase tracking-wide">Présents</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-emerald-50">
                  <p className="text-2xl font-bold font-display text-emerald-700">{Math.round((drawerSession.fill_rate || 0) * 100)}%</p>
                  <p className="text-[10px] text-emerald-600 uppercase tracking-wide">Taux</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input className="pl-8 h-9 text-sm" placeholder="Rechercher un participant…" value={drawerSearch} onChange={(e) => setDrawerSearch(e.target.value)} data-testid="drawer-search" />
                </div>
                <Button size="sm" variant="outline" onClick={exportCsv} disabled={!drawerAttendees.length} data-testid="drawer-export">
                  <Download className="h-3.5 w-3.5 mr-1" />CSV
                </Button>
              </div>
              <div className="space-y-1.5" data-testid="drawer-attendees">
                {drawerLoading && <p className="text-center text-slate-400 text-sm py-6">Chargement…</p>}
                {!drawerLoading && !filteredAttendees.length && <p className="text-center text-slate-400 text-sm py-6">Aucun participant scanné pour l'instant.</p>}
                {filteredAttendees.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                    <div className="h-9 w-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold text-sm shrink-0 overflow-hidden">
                      {a.photo ? <img src={a.photo} alt="" className="h-full w-full object-cover" /> : (a.firstName?.[0] || "?").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.firstName} {a.lastName}</p>
                      <p className="text-xs text-slate-500 truncate">{a.jobTitle}{a.company ? ` · ${a.company}` : ""}</p>
                    </div>
                    <p className="text-xs text-slate-400 shrink-0">{a.scannedAt?.slice(11, 16)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* QR modal */}
      <Dialog open={!!qrModal} onOpenChange={(v) => !v && setQrModal(null)}>
        <DialogContent className="max-w-md" data-testid="qr-modal">
          <DialogHeader>
            <DialogTitle>QR de session</DialogTitle>
            <DialogDescription>{qrModal?.session?.title}</DialogDescription>
          </DialogHeader>
          {qrModal?.qrImageUrl && (
            <div className="flex flex-col items-center gap-3 py-2">
              <img src={qrModal.qrImageUrl} alt="QR" className="h-64 w-64 rounded-xl border border-slate-200" />
              <p className="text-xs text-slate-500 text-center max-w-xs">Affichez ce QR à l'entrée de la salle. Les participants le scannent depuis l'app PlanItGreen.</p>
              <Button size="sm" variant="outline" onClick={() => { const w = window.open(); w.document.write(`<img src="${qrModal.qrImageUrl}" style="width:80vw;max-width:600px" /><script>setTimeout(()=>window.print(),300)</script>`); }} data-testid="qr-print">
                Imprimer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Alerts modal */}
      <Dialog open={alertsModal} onOpenChange={setAlertsModal}>
        <DialogContent className="max-w-lg" data-testid="alerts-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-600" />Alertes de capacité</DialogTitle>
            <DialogDescription>Sessions bientôt saturées ou en dépassement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {alertSessions.map((s) => {
              const fs = FILL_COLORS[s.fill_status] || FILL_COLORS.unknown;
              return (
                <div key={s.id} className={`p-3 rounded-xl border ${fs.bg}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-sm">{s.title}</p>
                    <span className={`text-xs font-bold ${fs.text}`}>{Math.round((s.fill_rate || 0) * 100)}%</span>
                  </div>
                  <p className="text-xs text-slate-500">{s.current_checkins} / {s.capacity_max} · {s.startTime} → {s.endTime}</p>
                </div>
              );
            })}
            {!alertSessions.length && <p className="text-center text-slate-400 py-6 text-sm">Aucune alerte active</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
